/**
 * Owner: Masa Bayu
 * File: qx-editor-page-face-objects.tsx
 * Date:Time: 2026-10-05 13:45
 * Description: Face live objects · SPSU ink color · center titles no track-fill.
 */

import { useRef, type CSSProperties, type ReactElement } from 'react';
import { paintTextFromTip } from '@qx/qx-render';
import { getRenderPipe } from '../feature-flags';
import { resolveCssFontFamily, resolveOcrTextFontFamily } from '../fonts/spsu-fonts';
import type {
  AssetLibrary,
  ImageElement,
  ShapeElement,
  SPSUElement,
  TextElement,
} from '../extract/spsu-types';

/** Accept Law Face gate · décor images behind · live text always on top. */
const FACE_TEXT_Z = 10;
const FACE_IMAGE_Z_MAX = 1;
/** SoT body / title ink on cream · never leave pdf.js white ghosts. */
export const FACE_BODY_INK = '#000000';

/** Prefer SPSU color; keep black when Eyes white/transparent would vanish on cream. */
export function resolveFaceInk(color: string | undefined): string {
  if (!color) return FACE_BODY_INK;
  const c = color.trim().toLowerCase();
  if (!c || c === 'transparent' || c === 'rgba(0, 0, 0, 0)' || c === 'rgba(0,0,0,0)') {
    return FACE_BODY_INK;
  }
  if (c === '#fff' || c === '#ffffff' || c === 'white') return FACE_BODY_INK;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const h = hex[1]!;
    const full =
      h.length === 3
        ? h
            .split('')
            .map((ch) => `${ch}${ch}`)
            .join('')
        : h;
    const r = Number.parseInt(full.slice(0, 2), 16);
    const g = Number.parseInt(full.slice(2, 4), 16);
    const b = Number.parseInt(full.slice(4, 6), 16);
    if (r >= 245 && g >= 245 && b >= 245) return FACE_BODY_INK;
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(c);
  if (rgb) {
    const r = Number(rgb[1]);
    const g = Number(rgb[2]);
    const b = Number(rgb[3]);
    if (r >= 245 && g >= 245 && b >= 245) return FACE_BODY_INK;
  }
  return color;
}

export function faceStackZIndex(element: SPSUElement): number {
  if (element.type === 'text') return FACE_TEXT_Z + Math.max(0, element.layer);
  if (element.type === 'image') return Math.min(Math.max(0, element.layer), FACE_IMAGE_Z_MAX);
  return element.layer;
}

export function boxStyle(element: SPSUElement): CSSProperties {
  const unit = element.bbox.unit;
  let height = element.bbox.height;
  // Single-line Eyes boxes are often fontSize-tall · descenders (g/y) need paint room.
  if (element.type === 'text') {
    const isParagraph =
      element.role === 'body' ||
      element.id.startsWith('el_ocr_para_') ||
      element.bbox.height > element.style.fontSize * 2.2;
    if (!isParagraph) {
      height = Math.max(height, element.style.fontSize * 1.35);
    }
  }
  return {
    position: 'absolute',
    left: `${element.bbox.x}${unit}`,
    top: `${element.bbox.y}${unit}`,
    width: `${element.bbox.width}${unit}`,
    height: `${height}${unit}`,
    zIndex: faceStackZIndex(element),
    transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
  };
}

type LiveObjectProps = {
  readonly element: SPSUElement;
  readonly assets: AssetLibrary;
  readonly editable: boolean;
  readonly onTextChange?: (elementId: string, newContent: string) => void;
};

export function LiveObject({
  element,
  assets,
  editable,
  onTextChange,
}: LiveObjectProps): ReactElement | null {
  const style = boxStyle(element);

  if (element.type === 'text') {
    return (
      <TextObject
        element={element}
        assets={assets}
        style={style}
        editable={editable}
        {...(onTextChange ? { onTextChange } : {})}
      />
    );
  }
  if (element.type === 'image') {
    return <ImageObject element={element} assets={assets} style={style} />;
  }
  if (element.type === 'shape') {
    return <ShapeObject element={element} style={style} />;
  }
  if (element.type === 'group') {
    return <div style={style} data-group-id={element.id} data-element-type="group" />;
  }
  return null;
}

type ShapeObjectProps = {
  readonly element: ShapeElement;
  readonly style: CSSProperties;
};

function ShapeObject({ element, style }: ShapeObjectProps): ReactElement {
  const stroke = element.stroke;
  const fill = element.fill;
  // PDF hairlines (<1 device px) still paint in Acrobat · floor to 1px for Face only.
  const widthPx = stroke && stroke.width > 0 ? Math.max(1, stroke.width) : 0;
  return (
    <div
      className="qx-shape-object"
      data-element-id={element.id}
      data-element-type="shape"
      data-shape={element.shape}
      data-qxeditor-element={element.id}
      style={{
        ...style,
        boxSizing: 'border-box',
        backgroundColor: fill?.color ?? 'transparent',
        borderStyle: widthPx > 0 ? 'solid' : undefined,
        borderWidth: widthPx > 0 ? widthPx : undefined,
        borderColor: widthPx > 0 ? stroke?.color ?? '#000000' : undefined,
        pointerEvents: 'none',
      }}
    />
  );
}

type TextObjectProps = {
  readonly element: TextElement;
  readonly assets: AssetLibrary;
  readonly style: CSSProperties;
  readonly editable: boolean;
  readonly onTextChange?: (elementId: string, newContent: string) => void;
};

function TextObject({ element, assets, style, editable, onTextChange }: TextObjectProps): ReactElement {
  const pipe = getRenderPipe();
  const isOcrText = element.id.startsWith('el_ocr_');
  const fontFamily = isOcrText
    ? resolveOcrTextFontFamily()
    : resolveCssFontFamily(element.style.fontFamily, assets);
  const role = element.role;
  const isBodyRole = role === 'body';
  const isTitleRole = role === 'title';
  const center = element.style.textAlign === 'center';
  /**
   * Multi-line OCR paras (el_ocr_para_*) · class only (wrap via CSS when needed).
   * R7 · remasure / plate-fit retired · paintTextFromTip only.
   */
  const isParagraph =
    element.id.startsWith('el_ocr_para_') ||
    (!isTitleRole &&
      !center &&
      element.bbox.height > element.style.fontSize * 2.2);
  const isDropCap = element.id.includes('dropcap') || /^Segala\b/i.test(element.content);
  const ref = useRef<HTMLDivElement | null>(null);
  const className = ['qx-text-object', isParagraph ? 'qx-paragraph-body' : '']
    .filter(Boolean)
    .join(' ');
  const faceLang = element.language ?? (isParagraph || isBodyRole ? 'ms' : undefined);
  const faceDir = element.direction ?? (isParagraph || isBodyRole ? 'ltr' : undefined);

  const ink = resolveFaceInk(element.style.color);
  const paint = paintTextFromTip({
    element: {
      id: element.id,
      content: element.content,
      bbox: {
        x: element.bbox.x,
        y: element.bbox.y,
        width: element.bbox.width,
        height: element.bbox.height,
      },
      style: {
        fontFamily,
        fontSize: element.style.fontSize,
        textAlign: element.style.textAlign,
      },
    },
    page: { width: 0, height: 0 },
  });

  const textStyle: CSSProperties = {
    fontFamily,
    fontSize: paint.fontSize,
    fontWeight: element.style.fontWeight,
    fontStyle: element.style.fontStyle,
    color: ink,
    WebkitTextFillColor: ink,
    textAlign: paint.textAlign,
    /* §448-S5 · `text-align-last: justify` ialah kontrak plat SATU-BARIS (absolute
     * placement). Pada plat berbilang baris ia meregangkan baris terakhir, sedangkan
     * baris terakhir sumber sentiasa ragged-left. */
    ...(isParagraph
      ? { textAlignLast: 'left' as const }
      : paint.textAlignLast !== undefined
        ? { textAlignLast: paint.textAlignLast }
        : {}),
    textDecoration: element.style.textDecoration?.join(' ') || 'none',
    whiteSpace: isParagraph ? 'normal' : (paint.whiteSpace ?? 'pre'),
    /* §448-S3 · tip mengisytihar pitch (nisbah) + indent baris pertama ·
     * sebelum ini dibuang → jarak baris jatuh ke `normal` (-16.6% pada p18). */
    ...(element.style.lineHeight !== undefined
      ? { lineHeight: element.style.lineHeight }
      : {}),
    ...(element.style.textIndent !== undefined && element.style.textIndent !== 0
      ? { textIndent: `${element.style.textIndent}${element.bbox.unit}` }
      : {}),
    overflow: 'visible',
    outline: 'none',
  };

  const layoutStyle: CSSProperties = {
    ...style,
    left: paint.left,
    top: paint.top,
    width: paint.width,
    ...(paint.height !== undefined ? { height: paint.height } : {}),
  };

  return (
    <div
      ref={ref}
      className={className}
      data-element-id={element.id}
      data-element-type="text"
      data-qxeditor-element={element.id}
      data-qxeditor-face-text=""
      data-role={role}
      data-text-align={paint.dataTextAlign}
      data-render-pipe={pipe}
      data-text-script={element.script}
      data-text-direction={faceDir}
      data-paragraph={isParagraph ? 'true' : undefined}
      data-dropcap={isDropCap ? 'true' : undefined}
      dir={faceDir}
      lang={faceLang}
      style={{
        ...layoutStyle,
        ...textStyle,
        cursor: editable ? 'text' : 'default',
      }}
      contentEditable={editable}
      spellCheck={false}
      suppressContentEditableWarning
      onBlur={(event) => {
        if (!onTextChange) return;
        onTextChange(element.id, event.currentTarget.textContent ?? '');
      }}
    >
      {element.content}
    </div>
  );
}

type ImageObjectProps = {
  readonly element: ImageElement;
  readonly assets: AssetLibrary;
  readonly style: CSSProperties;
};

function ImageObject({ element, assets, style }: ImageObjectProps): ReactElement {
  const asset = assets[element.assetId];

  if (!asset) {
    console.warn(`Asset ${element.assetId} not found for element ${element.id}`);
    return (
      <div
        className="qx-missing-asset"
        data-element-id={element.id}
        data-element-type="image"
        style={{
          ...style,
          backgroundColor: 'rgba(255, 0, 0, 0.12)',
          border: '1px dashed red',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          color: 'red',
        }}
      >
        MISSING: {element.assetId}
      </div>
    );
  }

  return (
    <img
      className="qx-image-object"
      data-element-id={element.id}
      data-element-type="image"
      data-asset-id={element.assetId}
      data-qxeditor-element={element.id}
      src={asset.src}
      alt={element.altText ?? ''}
      draggable={false}
      style={{
        ...style,
        display: 'block',
        objectFit: element.objectFit,
        maxWidth: 'none',
        pointerEvents: 'none',
      }}
    />
  );
}
